import { X } from "lucide-react";
import { useState } from "react";
import { useIntercom } from "react-use-intercom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { StepComponentProps } from "../step-config-file/types";

interface InfoCardData {
  title: string;
  copy: string | React.ReactNode;
  showExploreButton?: boolean;
  showSpeakToExpertButton?: boolean;
}

export const InfoCard = (props: StepComponentProps) => {
  const { showNewMessage } = useIntercom();
  const cardData = props.data as InfoCardData;
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleExit = () => {
    const exploreStepId = 11;
    const currentStepId = props.id;

    if (currentStepId < exploreStepId) {
      setIsTransitioning(true);

      props.moveToNextLocation({
        longitude: 146.16271,
        latitude: -18.65488,
        geocoder_attributes: {},
        zoomLevel: 13,
        targetStep: exploreStepId,
      });

      setTimeout(() => {
        props.goToStep(exploreStepId);
      }, 2500);
    }
  };

  const handleNext = () => {
    if (props.nextCoordinates && props.zoomLevel) {
      props.moveToNextLocation({
        ...props.nextCoordinates,
        geocoder_attributes: {},
        zoomLevel: props.zoomLevel,
        targetStep: props.id + 1,
      });
    }
    props.nextStep();
  };

  const handleBack = () => {
    if (props.previousCoordinates && props.previousZoomLevel) {
      props.moveToNextLocation({
        ...props.previousCoordinates,
        geocoder_attributes: {},
        zoomLevel: props.previousZoomLevel,
        targetStep: props.id - 1,
      });
    }
    props.backStep();
  };

  const handleSpeakToExpert = () => {
    showNewMessage(
      "I'd like to learn more about First Floor Elevation Insights",
    );
  };

  if (isTransitioning) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 z-50 left-4 right-4 md:center-over-content md:right-auto md:-translate-x-1/2 md:min-w-sm md:max-w-lg">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-xl">{cardData.title}</CardTitle>
        <Button
          variant="ghost"
          size="icon"
          aria-label="close"
          onClick={handleExit}
        >
          <X className="size-4" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {typeof cardData.copy === "string" ? (
          <p>{cardData.copy}</p>
        ) : (
          cardData.copy
        )}
      </CardContent>
      <CardFooter className="justify-between">
        {props.id === 1 ? (
          <Button variant="ghost" onClick={handleExit}>
            Exit
          </Button>
        ) : props.id > 1 ? (
          <Button variant="ghost" onClick={handleBack}>
            Back
          </Button>
        ) : null}
        {cardData.showSpeakToExpertButton && (
          <Button onClick={handleSpeakToExpert}>Talk to an expert</Button>
        )}
        {cardData.showExploreButton ? (
          <Button onClick={handleNext}>Explore</Button>
        ) : (
          <Button onClick={handleNext}>Next</Button>
        )}
      </CardFooter>
    </Card>
  );
};
